import { inject, Injectable } from '@angular/core';
import { CpwTaskRepository } from './cpw-task.repository';
import { CpwTask } from '../models/task.model';
import { TaskFilterEvent } from '../models/task-filters';
import { UserPositionDto, UserPositionFacade } from '@pf/platform-core/domain';
import { combineLatest, debounceTime, map, Observable, of } from 'rxjs';
import { cloneDeep, isEqual, merge } from 'lodash-es';
import { LoggerService } from '@pf/shared-services';

@Injectable()
export class CpwTaskStateService {
    private logger = inject(LoggerService);
    private repository = inject(CpwTaskRepository);
    private userPositionFacade = inject(UserPositionFacade);

    private contextModifiedLocally = false;

    tasks$ = this.repository.tasks$;
    activeTask$ = combineLatest([
        this.repository.activeTask$,
        this.userPositionFacade.active$,
    ]).pipe(
        debounceTime(10),
        map(([task, userPosition]) => {
            if (!task) return task;
            // Fresh page load: server userPosition is authoritative.
            // In-session: local task.context is authoritative.
            const mergedContext = this.contextModifiedLocally
                ? merge({}, userPosition?.context || {}, task.context || {})
                : merge({}, task.context || {}, userPosition?.context || {});

            // Sync merged context to the repository so subsequent updates
            // (e.g., updateTaskContext$) start from the merged state.
            if (!isEqual(task.context, mergedContext)) {
                this.repository.updateTask({
                    ...task,
                    context: mergedContext,
                });
            }

            return {
                ...task,
                context: mergedContext,
            };
        })
    );
    taskFilters$ = this.repository.taskFilters$;

    setTasks(tasks: CpwTask[]): void {
        this.repository.setTasks(tasks);
    }

    setActiveTask(taskId: string): void {
        this.contextModifiedLocally = false;
        this.repository.setActiveTask(taskId);
    }

    clearTasks() {
        this.contextModifiedLocally = false;
        this.clearActiveTask();
        this.repository.clearTasks();
    }

    claimActiveTask(): void {
        if (!this.repository.activeTask) {
            return;
        }
        this.repository.updateTask({
            ...this.repository.activeTask,
            claimedAt: new Date(),
        });
    }

    clearActiveTask() {
        this.repository.clearActiveTask();
    }

    getTaskFilters() {
        return this.repository.getTaskFilters();
    }

    setFilter(event: TaskFilterEvent) {
        if (this.repository.getTaskFilters()[event.filter] === event.value) {
            return;
        }
        this.repository.updateTaskFilter(event);
    }

    clearTaskFilters() {
        this.repository.clearTaskFilters();
    }

    getActiveTask<T extends CpwTask>() {
        return this.repository.activeTask as T | null;
    }

    updateTask(task: CpwTask) {
        this.repository.updateTask(task);
    }

    getTask<T extends CpwTask>(taskId: string): T | null {
        return this.repository.getTask(taskId) as T | null;
    }

    performWithActiveTask$<T extends CpwTask, TResult>(
        action: (activeTask: T) => Observable<TResult>,
        nullAction: Observable<TResult>
    ): Observable<TResult> {
        const activeTask = this.getActiveTask<T>();
        if (!activeTask) {
            return nullAction;
        }
        return action(activeTask);
    }

    updateTaskContext$<T extends object>(
        taskId: string,
        context: Partial<T>
    ): Observable<UserPositionDto | null> {
        const task = this.getTask(taskId);
        if (!task) {
            this.logger.warn(`Task ${taskId} not found. Context not updated.`);
            return of(null);
        }
        const newContext = {
            ...(task.context || {}),
            ...context,
        };
        this.contextModifiedLocally = true;
        this.repository.updateTask({
            ...task,
            context: newContext,
        });

        return this.userPositionFacade.updateContext$(cloneDeep(newContext));
    }
}
